import React from "react";
import { imagePreload, ImagePreloadPromiseResponse } from "../../../../../common/utils/image-preload";
import {CyClass} from "../../../../../common/cy-classes";
import {OrbitComponent} from "../../../base-components/orbit-component/orbit-component";
import {VideoTileBox} from "./boxes/video-tile.box";
import {AspectRatioContainerComponent} from "../../../raw-components/aspect-ratio-container/aspect-ratio-container.component";
import {VideoTileOverlayComponent} from "../video-tile-overlay/video-tile-overlay.component";
import {VideoTileOverlayBox} from "../video-tile-overlay/boxes/video-tile-overlay.box";
import {VideoTileStaticImageOverlayBox} from "../video-tile-static-image-overlay/video-tile-static-image-overlay.box";
import {VideoTileSkeletonContainerBox} from "../video-tile-skeleton-container/video-tile-skeleton-container.box";
import {VideoComponent} from "../video-component/video.component";
import { action, observable, makeObservable } from "mobx";
import {observer} from "mobx-react";
import {GameInfo} from "../../../../services/games/game-info";
import {SingleGameSkeletonComponent} from "../../../single-game/single-game-skeleton.component";
import {LIVE_STREAMING_ITEM} from "../../../../themes/css-global-classes";
import {TableSeatsComponent} from "../../../arena-host/new-live-casino-arena/live-casino-arena-grid/shared-components/table-seats.component";
import {RouletteDrawsComponent} from "../../../arena-host/new-live-casino-arena/live-casino-arena-grid/shared-components/roulette-draws.component";
import {IVideoPlayingService} from "../../../../services/video-live-stream/video-playing.service.interface";
import VideoRetryScreenComponent from "../video-retry-screen/video-retry-screen.component";
import {VIDEO_ITEM_TYPE} from "../../../single-game/video-template/single-game-video-template.component";
import VideoThumbnailImageComponent from "../video-thumbnail-image/video-thumbnail-image.component";
import VideoTileFooterComponent from "../video-tile-footer/video-tile-footer.component";
import {
    OrbitMarketingEventAnalyticsLabel
} from "../../../../services/analytics/analytics.service.interface";

export interface VideoTileComponentProps {
    gameInfo: GameInfo;
    isInCenterStage: boolean;
    inViewport: boolean;
    onOpenRealGame: () => void;
    videoPlayingService: IVideoPlayingService;
    videoItemType: VIDEO_ITEM_TYPE;
    isLastGametype?: boolean;
}

@observer
export class VideoTileComponent extends OrbitComponent<VideoTileComponentProps> {

    constructor(props: VideoTileComponentProps) {

        super(props);

        makeObservable(this);
    }

    private _posterImageLoadPromise: Promise<ImagePreloadPromiseResponse> | undefined = undefined;

    @observable.ref
    private _hasError: boolean = false;

    private _retryFn: (() => void) | undefined;

    @action.bound
    setHasError(value: boolean, retryFn?: (() => void) | undefined) {
        this._hasError = value;
        this._retryFn = retryFn;
    }

    @observable.ref
    private _posterImageLoaded = false;

    @action.bound
    private setPosterImageLoaded(value: boolean) {
        this._posterImageLoaded = value;
    }

    private onPosterImageLoaded = () => {
        this.setPosterImageLoaded(true);
    }

    @observable.ref
    private _isVideoLoading: boolean = true;

    @observable.ref
    private _isFirstTimeLoading: boolean = true;

    @action.bound
    setIsVideoLoading(value: boolean) {
        this._isVideoLoading = value;
        if (value === false) {
            this.firstTimeLoadingDone();
        }
    }

    @action.bound
    firstTimeLoadingDone() {
        if (this._isFirstTimeLoading === true) {
            this._isFirstTimeLoading = false;
        }
    }

    @observable.ref
    private _isWatchTimeLimitReached: boolean = false;

    @action.bound
    setIsWatchTimeLimitReached(value: boolean) {
        this._isWatchTimeLimitReached = value;
    }

    private _onPlayClickHandler = () => {
        this.props.onOpenRealGame();
        const gaCategory = this.props.videoPlayingService.gaCategory;
        this.services.analytics.sendOrbitMarketingEvent(gaCategory, 'PLAY_GAME', OrbitMarketingEventAnalyticsLabel.Click, this.props.gameInfo.gameType);
    };

    componentDidMount() {
        if (this.props.gameInfo.videoThumbnailUrl) {
            this._posterImageLoadPromise = imagePreload(this.props.gameInfo.videoThumbnailUrl);
            this._posterImageLoadPromise.then(this.onPosterImageLoaded);
        }
    }

    componentWillUnmount() {
        if (this._posterImageLoadPromise) {
            this._posterImageLoadPromise = undefined;
        }
    }

    private _renderPlayerOverlay() {
        if (!this._isWatchTimeLimitReached && !this._isVideoLoading) {
            return null;
        }

        let statusText = '';
        if (this._isWatchTimeLimitReached) {
            statusText = this.services.translation.translate('LIVE_STREAM_WATCH_TIME_REACHED');
        } else if (this._isVideoLoading) {
            statusText = this.services.translation.translate('LIVE_STREAM_VIDEO_LOADING');
        }

        return <VideoTileOverlayBox>{statusText}</VideoTileOverlayBox>;
    }

    private _renderRetryScreen() {
        return (
            <VideoRetryScreenComponent
                retryFn={this._retryFn}
                setHasError={this.setHasError}
            />
        )
    }

    private _renderPlayer() {
        return (
            <AspectRatioContainerComponent width={16} height={9}>
                {/* For the video element I cannot conditionally render it for this.props.isInCenterStage because when the video reaches end and the user
                swipes right/left it renders again and starts again */}
                <VideoComponent
                    gameInfo={this.props.gameInfo}
                    muted={this.props.videoPlayingService.isVideoMuted}
                    onError={this.setHasError}
                    onLoading={this.setIsVideoLoading}
                    onWatchTimeLimitReached={this.setIsWatchTimeLimitReached}
                    isInCenterStage={this.props.isInCenterStage}
                    inViewport={this.props.inViewport}
                    display={this.props.isInCenterStage && !this._hasError}
                    videoPlayingService={this.props.videoPlayingService}
                    isLastGametype={this.props.isLastGametype}
                />
                {((this.services.mediaQuery.xlAndAbove && !this.props.isInCenterStage) || this._hasError || (this.props.isInCenterStage && this._isFirstTimeLoading && this._posterImageLoaded)) ? this._renderVideoThumbnailImage(this._hasError && this.props.isInCenterStage) : this._renderPlayerOverlay()}
                {this._renderTileOverlayComponent()}
                {(this._hasError && this.props.isInCenterStage) ? this._renderRetryScreen() : null}
            </AspectRatioContainerComponent>
        );
    }

    private _renderTileOverlayComponent(): React.ReactNode {
        if (this.props.videoItemType === VIDEO_ITEM_TYPE.STATIC) {
            return (
                <VideoTileOverlayComponent
                    videoPlayingService={this.props.videoPlayingService}
                    videoPlaying={this.props.isInCenterStage && !this._hasError}
                />
            );
        }

        const tableInfo = this.services.liveTableFeeds.getTableInfo(this.props.gameInfo.gameType);

        return (
            <VideoTileOverlayComponent
                videoPlayingService={this.props.videoPlayingService}
                bottomLeftComponent={<TableSeatsComponent isLiveStreamOverlay tableInfo={tableInfo}/>}
                bottomRightComponent={<RouletteDrawsComponent tableInfo={tableInfo}/>}
                videoPlaying={this.props.isInCenterStage && !this._hasError}
            />
        )

    }

    private _renderVideoThumbnailImage(filterBlur: boolean) {
        return <VideoThumbnailImageComponent
            gameInfo={this.props.gameInfo}
            onLoad={this.firstTimeLoadingDone}
            onSettled={this.firstTimeLoadingDone}
            filterBlur={filterBlur}
        />
    }

    private _renderFooter() {
        return (
            <VideoTileFooterComponent
                onClickPlay={this._onPlayClickHandler}
                gameInfo={this.props.gameInfo}
                type={this.props.videoItemType === VIDEO_ITEM_TYPE.STATIC ? "jackpot" : "bets"}
            />
        );
    }

    private _renderSkeleton() {
        if (this._isFirstTimeLoading && !this._posterImageLoaded) {
            return (
                <VideoTileSkeletonContainerBox>
                    <SingleGameSkeletonComponent
                        templateType={LIVE_STREAMING_ITEM}
                        useSquareIcons={false}
                        placeholderImageSrc={''}
                    />
                </VideoTileSkeletonContainerBox>
            )
        }
    }

    private _renderStaticImageOverlay() {
        if (this.props.isInCenterStage || this.services.mediaQuery.lAndBelow) {
            return null;
        }
        return <VideoTileStaticImageOverlayBox/>;
    }


    render() {

        return (
            <VideoTileBox className={CyClass("cy-orbit-swiper-streaming-tile")}>
                {this._renderPlayer()}
                {this._renderFooter()}
                {this._renderSkeleton()}
                {this._renderStaticImageOverlay()}
            </VideoTileBox>
        );
    }
}
